import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";
import BookmarkButton from "@/components/BookmarkButton";
import {
  Copy, Check, ArrowLeft, Lock, Zap, Lightbulb, Target,
  ClipboardList, MessageSquare, Wrench, Info, Star, Shield, Award, Coins
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ── Star display (read-only) ──────────────────────────────────────────────────
const StarDisplay = ({ rating, size = 13 }) => (
  <div className="flex items-center gap-0.5">
    {[1,2,3,4,5].map(i => (
      <Star key={i} size={size}
        className={i <= Math.round(rating)
          ? "text-amber-400 fill-amber-400"
          : "text-slate-200 fill-slate-200"}
      />
    ))}
  </div>
);

// ── Star selector (interactive) ───────────────────────────────────────────────
const StarSelector = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1,2,3,4,5].map(i => (
        <button key={i} type="button"
          onClick={() => onChange(i)}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          className="p-0.5 transition-transform hover:scale-110"
          data-testid={`star-${i}`}
        >
          <Star size={26}
            className={(hover || value) >= i
              ? "text-amber-400 fill-amber-400"
              : "text-slate-300 fill-slate-300"}
          />
        </button>
      ))}
    </div>
  );
};

// ── Rate Author Section ───────────────────────────────────────────────────────
const RATE_CATEGORIES = [
  { key: "prompt_quality",   label: "Quality of Prompt" },
  { key: "business_value",   label: "Practical Business Value" },
  { key: "clarity",          label: "Clarity & Ease of Use" },
  { key: "depth",            label: "Depth of Expertise" },
  { key: "effectiveness",    label: "Overall Effectiveness" },
];

const InlineStarSelector = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <button key={i} type="button"
          onClick={() => onChange(i === value ? 0 : i)}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          className="p-0.5 hover:scale-110 transition-transform"
        >
          <Star size={18} className={(hover || value) >= i ? "text-amber-400 fill-amber-400" : "text-slate-300 fill-slate-300"} />
        </button>
      ))}
    </div>
  );
};

const RateAuthorSection = ({ contributorId, user }) => {
  const [open, setOpen] = useState(false);
  const [scores, setScores] = useState({});
  const [existing, setExisting] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [contrib, setContrib] = useState(null);

  useEffect(() => {
    axios.get(`${API}/contributors/${contributorId}`).then(r => setContrib(r.data)).catch(() => {});
    axios.get(`${API}/contributors/${contributorId}/my-rating`, { withCredentials: true })
      .then(r => { if (r.data.scores) { setExisting(r.data); setScores(r.data.scores); } })
      .catch(() => {});
  }, [contributorId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!Object.values(scores).some(s => s > 0)) { toast.error("Please rate at least one category."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contributors/${contributorId}/rate`, { scores }, { withCredentials: true });
      setExisting({ scores });
      setOpen(false);
      toast.success(existing ? "Rating updated!" : "Author rated!", { description: "Thanks for your feedback." });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit rating.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!contrib) return null;
  const isPromptlee = contrib.is_platform_author;
  const canRate = !contrib.user_id || contrib.user_id !== user?.user_id;
  if (!canRate) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="rate-author-section">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
        data-testid="toggle-rate-author-btn"
      >
        <div className="flex items-center gap-2">
          <Star size={14} className="text-amber-500 fill-amber-500" />
          <span className="font-semibold text-slate-800 text-sm">
            Rate the Author — {isPromptlee ? "Promptlee" : contrib.name}
          </span>
          {existing?.scores && <Badge className="text-xs bg-emerald-50 text-emerald-700 border-0">Rated</Badge>}
        </div>
        <span className="text-xs text-slate-400">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-6 pb-5 border-t border-slate-100">
          <p className="text-xs text-slate-500 mt-4 mb-4">Rate this author across expertise dimensions. All categories are optional.</p>
          <form onSubmit={handleSubmit} className="space-y-3">
            {RATE_CATEGORIES.map(cat => (
              <div key={cat.key} className="flex items-center justify-between">
                <span className="text-sm text-slate-700">{cat.label}</span>
                <InlineStarSelector value={scores[cat.key] || 0} onChange={v => setScores(p => ({ ...p, [cat.key]: v }))} />
              </div>
            ))}
            <div className="flex justify-between items-center pt-2">
              <Link to={`/contributor/${contributorId}`} className="text-xs text-blue-600 hover:underline">View author profile →</Link>
              <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={submitting} data-testid="submit-author-rating-btn">
                {submitting ? "Saving..." : existing ? "Update Rating" : "Submit Rating"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// ── Author Card (fetches contributor inline) ──────────────────────────────────
const AuthorCard = ({ contributorId, isAdmin }) => {
  const [contrib, setContrib] = useState(null);
  useEffect(() => {
    axios.get(`${API}/contributors/${contributorId}`).then(r => setContrib(r.data)).catch(() => {});
  }, [contributorId]);
  if (!contrib) return null;
  const isPromptlee = contrib.is_platform_author;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4" data-testid="author-card">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-base font-bold shrink-0 ${isPromptlee ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-700"}`}>
        {isPromptlee ? "W" : (contrib.name?.[0] || "C").toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/contributor/${contributorId}`} className="font-semibold text-sm text-slate-900 hover:text-blue-600 transition-colors">
            {contrib.name}
          </Link>
          {isPromptlee && (
            <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full font-semibold">
              <Shield size={10} /> Official Content
            </span>
          )}
          {(contrib.badges || []).includes("top_contributor") && (
            <span className="inline-flex items-center gap-1 text-xs bg-violet-50 text-violet-700 border border-violet-100 px-2 py-0.5 rounded-full font-semibold">
              <Award size={10} /> Top Contributor
            </span>
          )}
          {(contrib.badges || []).includes("highly_rated") && !isPromptlee && (
            <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 rounded-full font-semibold">
              <Star size={10} className="fill-amber-500" /> Highly Rated
            </span>
          )}
        </div>
        {contrib.title && <p className="text-xs text-slate-500 mt-0.5">{contrib.title}</p>}
      </div>
      {contrib.overall_rating > 0 && (
        <div className="text-right shrink-0">
          <div className="flex items-center gap-1 justify-end">
            <Star size={12} className="text-amber-400 fill-amber-400" />
            <span className="text-sm font-bold text-slate-800">{contrib.overall_rating}</span>
          </div>
          <p className="text-xs text-slate-400">{contrib.rating_count} ratings</p>
        </div>
      )}
    </div>
  );
};

// ── Quick-rate widget (sits in the header card, no scroll needed) ─────────────
const QuickRateWidget = ({ userReview, onRate, submitting }) => {
  const [hover, setHover]     = useState(0);
  const [editing, setEditing] = useState(false);
  const current = userReview?.rating || 0;

  const handleStar = async (star) => {
    await onRate(star);
    setEditing(false);
  };

  if (userReview && !editing) {
    return (
      <div className="flex items-center gap-2" data-testid="quick-rate-display">
        <span className="text-xs text-slate-400">Your rating:</span>
        <div className="flex items-center gap-0.5">
          {[1,2,3,4,5].map(i => (
            <Star key={i} size={13}
              className={i <= current ? "text-amber-400 fill-amber-400" : "text-slate-200 fill-slate-200"} />
          ))}
        </div>
        <button
          onClick={() => setEditing(true)}
          className="text-xs text-blue-500 hover:text-blue-700 hover:underline ml-1"
          data-testid="quick-rate-change-btn"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2" data-testid="quick-rate-widget">
      <span className="text-xs text-slate-400">
        {userReview ? "Update:" : "Rate this prompt:"}
      </span>
      <div className="flex items-center gap-0.5">
        {[1,2,3,4,5].map(i => (
          <button
            key={i}
            type="button"
            disabled={submitting}
            onClick={() => handleStar(i)}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(0)}
            className="p-0.5 transition-transform hover:scale-125 disabled:opacity-50 disabled:cursor-wait"
            data-testid={`quick-star-${i}`}
          >
            <Star size={17}
              className={(hover || 0) >= i ? "text-amber-400 fill-amber-400" : "text-slate-300 fill-slate-300"} />
          </button>
        ))}
      </div>
      {submitting && <span className="text-xs text-slate-400 animate-pulse">Saving…</span>}
    </div>
  );
};

const TOOL_STYLES = {
  "ChatGPT":           { btn: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100",  active: "bg-emerald-600 text-white border-emerald-600", dot: "bg-emerald-500", note: "bg-emerald-50 border-emerald-100 text-emerald-700" },
  "Claude":            { btn: "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100",      active: "bg-orange-600 text-white border-orange-600",    dot: "bg-orange-500",  note: "bg-orange-50 border-orange-100 text-orange-700" },
  "Gemini":            { btn: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100",              active: "bg-blue-600 text-white border-blue-600",        dot: "bg-blue-500",    note: "bg-blue-50 border-blue-100 text-blue-700" },
  "Microsoft Copilot": { btn: "bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100",                  active: "bg-sky-600 text-white border-sky-600",          dot: "bg-sky-500",     note: "bg-sky-50 border-sky-100 text-sky-700" },
  "Perplexity":        { btn: "bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100",              active: "bg-teal-600 text-white border-teal-600",        dot: "bg-teal-500",    note: "bg-teal-50 border-teal-100 text-teal-700" },
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-6">
    <div className="flex items-center gap-2 mb-4">
      <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon size={14} className="text-blue-600" />
      </div>
      <h3 className="font-semibold text-slate-900">{title}</h3>
    </div>
    {children}
  </div>
);

const ApplicationPage = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isFavourite, setIsFavourite] = useState(false);

  // Prompt variation state: null = universal/main prompt
  const [activeVariant, setActiveVariant] = useState(null);
  const [copied, setCopied] = useState(false);

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [userReview, setUserReview] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [appRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/apps/${appId}`, { withCredentials: true }),
          axios.get(`${API}/apps/${appId}/reviews`),
        ]);
        setApp(appRes.data);
        setReviews(reviewsRes.data);
        // Check favourite status if logged in
        if (user) {
          try {
            const ids = await axios.get(`${API}/favourites/ids`, { withCredentials: true });
            setIsFavourite(ids.data.includes(appId));
          } catch {}
          // Pre-fill if user already reviewed
          const existing = reviewsRes.data.find(r => r.user_id === user.user_id);
          if (existing) {
            setUserReview(existing);
            setReviewRating(existing.rating);
            setReviewComment(existing.comment || "");
          }
        }
      } catch {
        setError("Application not found");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [appId, user]);

  const variations = app?.prompt_variations || {};
  const variationKeys = Object.keys(variations); // e.g. ["ChatGPT", "Claude", "Gemini"]
  const hasVariations = variationKeys.length > 0;

  // Determine active prompt text to show/copy
  const activePromptText = activeVariant && variations[activeVariant]
    ? (typeof variations[activeVariant] === "string"
        ? variations[activeVariant]
        : variations[activeVariant].prompt)
    : app?.main_prompt;

  const activeNotes = activeVariant && variations[activeVariant]
    ? (typeof variations[activeVariant] === "string" ? null : variations[activeVariant].notes || null)
    : null;

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewRating) { toast.error("Please select a star rating."); return; }
    setReviewSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/apps/${appId}/reviews`,
        { rating: reviewRating, comment: reviewComment },
        { withCredentials: true }
      );
      setApp(prev => ({ ...prev, avg_rating: res.data.avg_rating, review_count: res.data.review_count }));
      setUserReview(res.data.review);
      // Update reviews list
      setReviews(prev => {
        const filtered = prev.filter(r => r.user_id !== user.user_id);
        return [res.data.review, ...filtered];
      });
      toast.success(userReview ? "Review updated!" : "Review submitted!", { description: "Thanks for your feedback." });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review.");
    } finally {
      setReviewSubmitting(false);
    }
  };

  // Quick one-tap rating from header (no comment required)
  const handleQuickRate = async (rating) => {
    if (!user) { navigate("/auth"); return; }
    setReviewSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/apps/${appId}/reviews`,
        { rating, comment: reviewComment || "" },
        { withCredentials: true }
      );
      setApp(prev => ({ ...prev, avg_rating: res.data.avg_rating, review_count: res.data.review_count }));
      setUserReview(res.data.review);
      setReviewRating(rating);
      setReviews(prev => {
        const filtered = prev.filter(r => r.user_id !== user.user_id);
        return [res.data.review, ...filtered];
      });
      toast.success(userReview ? "Rating updated!" : "Rating saved!", {
        description: "Thanks for your feedback.",
        duration: 2500,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save rating.");
    } finally {
      setReviewSubmitting(false);
    }
  };

  const isLocked = app?.locked ?? false;
  const isPremium = app?.is_premium ?? false;
  const creditRequired = app?.credit_required ?? false;
  const userCredits = app?.user_credits ?? user?.credits ?? 0;
  const creditCost = app?.credit_cost || 0;

  const handleCopy = useCallback(async (source = "header") => {
    if (isLocked) {
      navigate(user ? "/pricing" : "/auth");
      return;
    }
    if (!activePromptText) return;
    // If premium accessed via credits, deduct credits first
    if (app?.credit_required) {
      try {
        const res = await axios.post(`${API}/apps/${appId}/copy`, {}, { withCredentials: true });
        setApp(prev => ({ ...prev, user_credits: res.data.credits_remaining }));
        const cost = res.data.credits_deducted;
        navigator.clipboard.writeText(activePromptText).then(() => {
          setCopied(source);
          setTimeout(() => setCopied(false), 2000);
          toast.success("Prompt copied!", {
            description: cost > 0
              ? `${cost} credit${cost !== 1 ? "s" : ""} used — ${res.data.credits_remaining} remaining.`
              : "Copied to clipboard.",
            duration: 3500,
          });
        }).catch(() => toast.error("Copy failed", { description: "Please copy manually." }));
      } catch (err) {
        if (err.response?.status === 402) {
          toast.error("Insufficient credits", { description: "Add more credits via a gift code or referrals." });
          navigate("/dashboard");
        } else {
          toast.error("Copy failed");
        }
      }
      return;
    }
    // Free or plan-based access
    navigator.clipboard.writeText(activePromptText).then(() => {
      setCopied(source);
      setTimeout(() => setCopied(false), 2000);
      axios.post(`${API}/apps/${appId}/copy`, {}, { withCredentials: true }).catch(() => {});
      const toolLabel = activeVariant ? `${activeVariant} version` : "Universal prompt";
      toast.success("Prompt copied!", {
        description: `${toolLabel} for "${app?.title}" is in your clipboard.`,
        duration: 3000,
      });
    }).catch(() => {
      toast.error("Copy failed", { description: "Please select and copy the text manually." });
    });
  }, [app, activePromptText, activeVariant, appId, isLocked, navigate, user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="max-w-4xl mx-auto px-4 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (error || !app) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 mb-4">{error || "Application not found"}</p>
          <Button onClick={() => navigate(-1)} variant="outline">Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>

        {/* Header Card */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <div className="flex flex-wrap items-start gap-3 mb-4">
            <Badge className="text-xs border-0 bg-blue-100 text-blue-700 capitalize">{app.function_id}</Badge>
            <Badge className="text-xs border-0 bg-slate-100 text-slate-600">{app.category}</Badge>
            <Badge className={`text-xs border-0 ${isPremium ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
              {isPremium ? `${app.credit_cost} credits` : "FREE"}
            </Badge>
            {/* Bookmark */}
            {user && (
              <div className="ml-auto">
                <BookmarkButton
                  appId={appId}
                  initialState={isFavourite}
                  onToggle={(_, state) => setIsFavourite(state)}
                  size={20}
                  data-testid="app-page-bookmark-btn"
                />
              </div>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">{app.title}</h1>
          <p className="text-slate-600 leading-relaxed">{app.business_context}</p>

          {/* ── Inline rating row ── */}
          <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-x-4 gap-y-2">
            {/* Aggregate avg (show when at least 1 review exists) */}
            {app.review_count > 0 && (
              <div className="flex items-center gap-1.5">
                <StarDisplay rating={app.avg_rating} size={14} />
                <span className="text-sm font-semibold text-slate-700">{app.avg_rating}</span>
                <span className="text-sm text-slate-400">({app.review_count} {app.review_count === 1 ? "review" : "reviews"})</span>
              </div>
            )}

            {/* Separator dot when both sections show */}
            {app.review_count > 0 && (user || true) && (
              <span className="text-slate-200 hidden sm:inline">|</span>
            )}

            {/* Interactive quick-rate for logged-in users on accessible prompts */}
            {user && !isLocked && (
              <QuickRateWidget
                userReview={userReview}
                onRate={handleQuickRate}
                submitting={reviewSubmitting}
              />
            )}

            {/* No-rating placeholder (first to be rated) */}
            {app.review_count === 0 && user && !isLocked && (
              <span className="text-xs text-slate-400 italic">Be the first to rate</span>
            )}
          </div>
        </div>

        {/* ── Author Card ── */}
        {app.contributor_id && (
          <AuthorCard contributorId={app.contributor_id} isAdmin={app.created_by_admin} />
        )}

        <div className="grid gap-4">
          <Section icon={Target} title="When to Use">
            <p className="text-slate-700 text-sm leading-relaxed">{app.when_to_use}</p>
          </Section>

          <Section icon={ClipboardList} title="Input Required">
            <p className="text-slate-700 text-sm leading-relaxed">{app.input_required}</p>
          </Section>

          {/* ── AI Prompt Section ── */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            {/* Header row */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center">
                  <MessageSquare size={14} className="text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-900">AI Prompt</h3>
                {!isLocked && <Badge className="text-xs border-0 bg-blue-50 text-blue-600">Ready to Copy</Badge>}
                {isLocked  && <Badge className="text-xs border-0 bg-amber-50 text-amber-600"><Lock size={10} className="mr-1 inline" />Premium</Badge>}
              </div>
              <Button
                size="sm"
                onClick={() => handleCopy("header")}
                className={isLocked
                  ? "bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200"
                  : copied === "header"
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }
                data-testid="copy-prompt-btn"
              >
                {isLocked
                  ? <><Lock size={14} className="mr-1.5" /> Unlock to Copy</>
                  : copied === "header"
                    ? <><Check size={14} className="mr-1.5" /> Copied!</>
                    : <><Copy size={14} className="mr-1.5" /> Copy Prompt</>
                }
              </Button>
            </div>

            {/* Tool Variant Selector — shown only when variations exist */}
            {!isLocked && hasVariations && (
              <div className="px-6 pt-4 pb-2 border-b border-slate-50">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2.5">
                  Select prompt version for your AI tool
                </p>
                <div className="flex flex-wrap gap-2">
                  {/* Universal (default) */}
                  <button
                    onClick={() => setActiveVariant(null)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${
                      !activeVariant
                        ? "bg-slate-800 text-white border-slate-800"
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                    }`}
                    data-testid="variant-universal-btn"
                  >
                    Universal
                  </button>
                  {variationKeys.map(tool => {
                    const style = TOOL_STYLES[tool] || TOOL_STYLES["ChatGPT"];
                    return (
                      <button
                        key={tool}
                        onClick={() => setActiveVariant(tool)}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${
                          activeVariant === tool ? style.active : style.btn
                        }`}
                        data-testid={`variant-${tool.toLowerCase().replace(/\s+/g, "-")}-btn`}
                      >
                        {tool} Version
                      </button>
                    );
                  })}
                </div>
                {activeVariant && (
                  <p className="text-xs text-slate-400 mt-2">
                    Showing prompt optimised for <strong>{activeVariant}</strong>
                  </p>
                )}
              </div>
            )}

            {/* Prompt Body */}
            <div className="relative">
              {isLocked ? (
                <div className="p-6">
                  <div className="relative min-h-[260px]">
                    <div className="prompt-box text-slate-400 text-base md:text-sm leading-relaxed blur-sm select-none">
                      Act as a senior professional. This is a premium prompt that requires an active subscription to access. Upgrade your plan to unlock this application and copy the full structured AI prompt...
                    </div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="bg-white/95 rounded-xl p-6 text-center border border-slate-200 shadow-sm">
                        <Lock size={24} className="text-blue-600 mx-auto mb-3" />
                        <h4 className="font-semibold text-slate-900 mb-2">Premium Application</h4>
                        <p className="text-sm text-slate-600 mb-4 max-w-xs">
                          {user
                            ? `You need ${creditCost} credits to copy this prompt. You have ${userCredits} credits.`
                            : "Sign in or upgrade to copy this premium prompt"}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2 justify-center">
                          <Button
                            onClick={() => user ? navigate("/pricing") : navigate("/auth")}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid="upgrade-to-unlock-btn"
                          >
                            <Zap size={14} className="mr-1.5" />
                            {user ? "Upgrade Plan" : "Sign In to Copy"}
                          </Button>
                          {user && (
                            <Button
                              variant="outline"
                              onClick={() => navigate("/dashboard")}
                              className="border-amber-200 text-amber-700 hover:bg-amber-50"
                              data-testid="get-credits-btn"
                            >
                              <Coins size={14} className="mr-1.5" />
                              Get Credits
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6 relative group">
                  <pre
                    className="prompt-box text-slate-700 text-base md:text-sm leading-relaxed whitespace-pre-wrap pr-14"
                    data-testid="prompt-text"
                  >
                    {activePromptText}
                  </pre>
                  {/* Floating inline copy button */}
                  <button
                    onClick={() => handleCopy("inline")}
                    title="Copy to clipboard"
                    className={`absolute top-5 right-5 p-2 rounded-lg border transition-all ${
                      copied === "inline"
                        ? "bg-emerald-50 border-emerald-200 text-emerald-600"
                        : "bg-white border-slate-200 text-slate-400 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 opacity-0 group-hover:opacity-100"
                    }`}
                    data-testid="copy-prompt-inline-btn"
                  >
                    {copied === "inline"
                      ? <Check size={15} />
                      : <Copy size={15} />
                    }
                  </button>
                </div>
              )}
            </div>

            {/* Bottom copy bar — always visible for unlocked prompts */}
            {!isLocked && (
              <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                <p className="text-xs text-slate-400">
                  {creditRequired
                    ? <span className="flex items-center gap-1 text-amber-600"><Coins size={11} /> {creditCost} credit{creditCost !== 1 ? "s" : ""} per copy • {userCredits} remaining</span>
                    : activeVariant ? `${activeVariant}-optimised version` : "Universal version — works with any AI tool"
                  }
                </p>
                <button
                  onClick={() => handleCopy("bottom")}
                  className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${
                    copied === "bottom"
                      ? "bg-emerald-50 border-emerald-200 text-emerald-600"
                      : creditRequired
                        ? "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
                        : "bg-white border-slate-200 text-slate-500 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50"
                  }`}
                  data-testid="copy-prompt-bottom-btn"
                >
                  {copied === "bottom" ? <Check size={12} /> : creditRequired ? <Coins size={12} /> : <Copy size={12} />}
                  {copied === "bottom" ? "Copied!" : creditRequired ? `Copy (${creditCost} cr)` : "Copy to clipboard"}
                </button>
              </div>
            )}

            {/* Tool-specific notes */}
            {!isLocked && activeNotes && (
              <div className={`mx-6 mb-5 p-3 rounded-lg border text-xs leading-relaxed ${
                TOOL_STYLES[activeVariant]?.note || "bg-slate-50 border-slate-100 text-slate-600"
              }`}>
                <span className="font-semibold">Tip for {activeVariant}: </span>
                {activeNotes}
              </div>
            )}
          </div>

          <Section icon={Info} title="Expected Output">
            <p className="text-slate-700 text-sm leading-relaxed">{app.expected_output}</p>
          </Section>

          {app.pro_tips?.length > 0 && (
            <Section icon={Lightbulb} title="Pro Tips">
              <ul className="space-y-2">
                {app.pro_tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-amber-600 text-xs font-bold">{i + 1}</span>
                    </span>
                    {tip}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Recommended Tools — Rich Section */}
          {app.recommended_tools?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-6 py-4 border-b border-slate-100">
                <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Wrench size={14} className="text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-900">Recommended AI Tools</h3>
                {app.tool_category && (
                  <span className="ml-auto text-xs bg-slate-100 text-slate-500 px-2.5 py-1 rounded-full capitalize">
                    {app.tool_category.replace("_", " ")}
                  </span>
                )}
              </div>
              <div className="p-6">
                {app.primary_tool && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Primary Tool</p>
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border ${TOOL_STYLES[app.primary_tool]?.btn || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      <span className={`w-2 h-2 rounded-full ${TOOL_STYLES[app.primary_tool]?.dot || "bg-slate-400"}`} />
                      {app.primary_tool}
                      {hasVariations && variationKeys.includes(app.primary_tool) && (
                        <span className="text-xs opacity-70 ml-1">(version available)</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      {{
                        "ChatGPT":           "Best for complex reasoning, structured analysis, and comprehensive business outputs with clear formatting.",
                        "Claude":            "Excellent for nuanced writing, long documents, legal review, and detailed analytical reports.",
                        "Gemini":            "Strong for research tasks, real-time knowledge, and tasks requiring current market information.",
                        "Microsoft Copilot": "Ideal for teams using Microsoft 365 — integrates directly with Word, Excel, Teams, and Outlook.",
                        "Perplexity":        "Best for real-time market research with cited sources and fact-checked business intelligence.",
                      }[app.primary_tool] || "Recommended for this application type."}
                    </p>
                  </div>
                )}
                {app.recommended_tools.length > 1 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Also Works With</p>
                    <div className="flex flex-wrap gap-2">
                      {app.recommended_tools.filter(t => t !== app.primary_tool).map(tool => (
                        <Badge key={tool} variant="outline" className="text-sm px-3 py-1 font-medium">
                          {tool}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      Results may vary slightly across tools — the core output will be equivalent.
                      {hasVariations && " Select a tool version above to see the optimised prompt."}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {app.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {app.tags.map(tag => (
                <span key={tag} className="text-xs bg-slate-100 text-slate-500 px-3 py-1 rounded-full">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* ── Ratings & Reviews ── */}          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="reviews-section">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-amber-50 rounded-lg flex items-center justify-center">
                  <Star size={14} className="text-amber-500 fill-amber-500" />
                </div>
                <h3 className="font-semibold text-slate-900">Ratings & Reviews</h3>
              </div>
              {app.review_count > 0 && (
                <div className="flex items-center gap-2">
                  <StarDisplay rating={app.avg_rating} size={14} />
                  <span className="text-sm font-bold text-slate-900">{app.avg_rating}</span>
                  <span className="text-xs text-slate-400">/ 5 &middot; {app.review_count} {app.review_count === 1 ? "review" : "reviews"}</span>
                </div>
              )}
            </div>

            {/* Review Form */}
            {user && !isLocked && (
              <div className="px-6 py-5 border-b border-slate-100 bg-slate-50">
                <p className="text-sm font-semibold text-slate-700 mb-3">
                  {userReview ? "Update your review" : "Leave a review"}
                </p>
                <form onSubmit={handleSubmitReview} className="space-y-3">
                  <div>
                    <StarSelector value={reviewRating} onChange={setReviewRating} />
                    {reviewRating > 0 && (
                      <p className="text-xs text-slate-400 mt-1">
                        {["", "Poor", "Fair", "Good", "Very Good", "Excellent"][reviewRating]}
                      </p>
                    )}
                  </div>
                  <textarea
                    placeholder="Share your experience with this prompt... (optional)"
                    value={reviewComment}
                    onChange={e => setReviewComment(e.target.value)}
                    rows={3}
                    maxLength={500}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 placeholder-slate-400 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    data-testid="review-comment-input"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">{reviewComment.length}/500</span>
                    <Button
                      type="submit"
                      size="sm"
                      disabled={reviewSubmitting || !reviewRating}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="submit-review-btn"
                    >
                      {reviewSubmitting ? "Submitting..." : userReview ? "Update Review" : "Submit Review"}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {!user && (
              <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 text-center">
                <p className="text-sm text-slate-500">
                  <button onClick={() => navigate("/auth")} className="text-blue-600 font-medium hover:underline">Sign in</button> to leave a review
                </p>
              </div>
            )}

            {/* Reviews List */}
            <div className="divide-y divide-slate-100">
              {reviews.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <Star size={28} className="text-slate-200 fill-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No reviews yet. Be the first to review!</p>
                </div>
              ) : (
                reviews.map(r => (
                  <div key={r.review_id} className="px-6 py-4" data-testid={`review-${r.review_id}`}>
                    <div className="flex items-start justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
                          {r.user_name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <span className="text-sm font-semibold text-slate-800">{r.user_name}</span>
                        {r.user_id === user?.user_id && (
                          <Badge className="text-xs border-0 bg-blue-50 text-blue-600 py-0">You</Badge>
                        )}
                      </div>
                      <span className="text-xs text-slate-400">
                        {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                    </div>
                    <StarDisplay rating={r.rating} size={12} />
                    {r.comment && <p className="text-sm text-slate-600 mt-2 leading-relaxed">{r.comment}</p>}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ── Rate the Author ── */}
          {app.contributor_id && user && (
            <RateAuthorSection
              contributorId={app.contributor_id}
              user={user}
              appId={appId}
            />
          )}

        </div>
      </div>
    </div>
  );
};

export default ApplicationPage;
